import qs from 'qs';
import { getLocale, getLocaleFromUrlOrState } from '../../util/locales/locales';
import { HttpService } from '../http/http.service';
import { AuthStorageService } from './auth-storage.service';
import { AuthResponse, UserInfo, UserInfoResponseDto } from './auth.model';
import { CryptoUtils } from './crypto.utils';

import { getQueryParam } from '../../util/query/query-parameters';
import {
  AUTH_AUTHORIZE_URL,
  AUTH_CLIENT_ID,
  AUTH_LOGOUT_URL,
  AUTH_REDIRECT_URI,
  AUTH_SCOPE,
  AUTH_TOKEN_URL,
  AUTH_TOKEN_WARP_URL,
  OPEN_ID_CONFIG_URL,
} from './auth-constants';
import { getHttpService, getHttpServiceWithOptions } from '../http/http.factory';
import axios from 'axios';
import { Logger } from '../../feature/logger/logger';

export interface WarpTokenResponse {
  id_token: string;
}

export interface TokenValidationResult {
  success: boolean;
  error?: Error;
}

/**
 * Possible states during user authentication
 *
 * - empty: nothing in the local storage, user needs to login with their username and password
 * - logged-in: the user is successfully logged in but a token hasn't been requested yet
 * - loading-token: get token request is in progress
 * - auth-completed: the user has successfully got a token
 * - auth-error: there has been an error
 * - logging-out: the user is logging out
 * - auth-in-progress: authentication is in progress
 */
export type AuthState = 'empty' | 'logged-in' | 'auth-in-progress' | 'loading-token' | 'auth-completed' | 'auth-error' | 'logging-out';

type AuthStateListener = (state: AuthState) => void;

export class AuthService {
  private static readonly _useWarpAuth: boolean = process.env.NEXT_PUBLIC_WARP_AUTH_ENABLED === 'true';
  private static instance: AuthService | null = null;
  private currentState: AuthState = 'empty';
  private readonly listeners: Set<AuthStateListener> = new Set();

  private constructor() {
    if (AuthService.instance) {
      // We need a singleton for the auth state
      throw new Error('Cannot create multiple instances of AuthService');
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  setState(state: AuthState) {
    this.currentState = state;
    this.listeners.forEach((listener) => listener(state));
  }

  getState(): AuthState {
    return this.currentState;
  }

  subscribe(listener: AuthStateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  static async getToken(queryCode: string): Promise<AuthResponse | undefined | unknown> {
    const body = AuthService.getTokenBody(queryCode);
    return body ? AuthService.requestToken(body) : Promise.resolve(undefined);
  }

  static async logout(): Promise<void> {
    const locale = getLocale();

    let logoutUrl = AUTH_LOGOUT_URL;
    if (locale.RFC5646 && /\{locale}/.test(logoutUrl)) {
      logoutUrl = logoutUrl.replace('{locale}', locale.RFC5646?.toLowerCase());
    }

    if (AuthService._useWarpAuth) {
      await getHttpServiceWithOptions(process.env.NEXT_PUBLIC_WARP_BASE_URL, true).post('/logout');
    }

    const idToken = AuthStorageService.getIdToken();
    if (idToken) {
      const url = new URL(logoutUrl);
      url.searchParams.set('id_token_hint', idToken);
      url.searchParams.set('state', qs.stringify({ targetUrl: AuthService.getTargetUrl() }));
      logoutUrl = url.toString();

      AuthStorageService.clearUserInfo();
      AuthStorageService.clearSession();

      window.location.href = logoutUrl;
    }
  }

  static async getUserInfoFromCustomerProfile(): Promise<UserInfo> {
    const httpService = new HttpService(undefined, AuthService._useWarpAuth);
    const httpServiceWoCredentials = new HttpService(undefined, false);

    const { userinfo_endpoint } = await httpServiceWoCredentials.get<{ userinfo_endpoint: string }>(OPEN_ID_CONFIG_URL);
    const userInfo = await httpService.get<UserInfoResponseDto>(
      !AuthService._useWarpAuth ? userinfo_endpoint : (process.env.NEXT_PUBLIC_WARP_BASE_URL as string),
      AuthService._useWarpAuth ? { headers: { 'x-absolute-api-url': userinfo_endpoint, 'x-client-id': AUTH_CLIENT_ID } } : {}
    );

    return {
      ...userInfo,
      anonymousId: CryptoUtils.hashWithSalt(userInfo.stihl_id),
    };
  }

  static async hasValidToken(): Promise<boolean> {
    Logger.debug('(hasValidToken fn)', 'Checking token validity via WARP status endpoint');
    return getHttpServiceWithOptions(process.env.NEXT_PUBLIC_WARP_BASE_URL, true)
      .get<{ status: string }>('/status')
      .then((res) => res.status === 'ok');
  }

  static logIn(redirectPath?: string): void {
    Logger.debug('(logIn fn)', 'Initiating login flow');
    AuthStorageService.clearUserInfo();
    AuthStorageService.clearSession();

    const pkceData = CryptoUtils.createPKCEData(redirectPath);
    AuthStorageService.savePkceFlowData(pkceData);
    const locale = getLocaleFromUrlOrState();
    let url = `${AUTH_AUTHORIZE_URL}&code_challenge=${pkceData.codeChallenge}&state=${encodeURIComponent(pkceData.state)}&ui_locales=${
      locale.RFC5646
    }`;
    const idTokenHint = getQueryParam('id_token_hint');
    if (idTokenHint) {
      url += `&id_token_hint=${idTokenHint}`;
    }

    Logger.debug('(logIn fn)', 'Redirecting to login URL', url);
    window.location.href = url;
  }

  static async canGetToken(queryCode?: string, queryState?: string): Promise<TokenValidationResult> {
    const errorResponse = (message: string): TokenValidationResult => ({
      success: false,
      error: new Error(message),
    });

    try {
      const { csrf, codeVerifier } = AuthStorageService.getPkceFlowData();

      if (!codeVerifier) {
        Logger.debug('(canGetToken fn)', 'Code verifier is missing.');
        return errorResponse('Code verifier is missing.');
      }

      if (!csrf || csrf !== queryState?.split('|')[1]) {
        Logger.debug('(canGetToken fn)', 'CSRF mismatch: expected CSRF does not match the one from the query state.');
        return errorResponse('CSRF mismatch: expected CSRF does not match the one from the query state.');
      }

      if (!queryCode) {
        Logger.debug('canGetToken', 'Query code is missing.');
        return errorResponse('Query code is missing.');
      }

      const tokenPresent = await AuthService.checkTokenPresence();
      if (tokenPresent) {
        Logger.debug('(canGetToken fn)', 'ID token is already present.');
        return errorResponse('ID token is already present.');
      }

      return { success: true };
    } catch (error) {
      Logger.debug('(canGetToken fn)', `An error occurred while validating the state: ${error.message}`);
      return errorResponse(`An error occurred while validating the state: ${error.message}`);
    }
  }

  static async checkTokenPresence(): Promise<boolean> {
    Logger.debug('(checkTokenPresence fn)');
    const idToken = AuthStorageService.getIdToken();

    if (!idToken) {
      Logger.debug('(checkTokenPresence fn)', 'No ID token found');
      return false;
    }

    if (AuthService._useWarpAuth) {
      const hasValidToken = await AuthService.hasValidToken();
      Logger.debug('(checkTokenPresence fn)', `Token validity: ${hasValidToken}`);
      return hasValidToken;
    }

    return true;
  }

  private static async requestTokenFromWarp(requestBody: Record<string, string>) {
    try {
      const { data } = await axios.post<WarpTokenResponse>(AUTH_TOKEN_WARP_URL, qs.stringify(requestBody), {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;',
        },
      });

      AuthStorageService.clearPkceFlowData();
      AuthStorageService.saveWarpAuthResponse(data.id_token);
      return data;
    } catch (error) {
      console.error('Error requesting token:', error);
      throw error;
    }
  }

  private static async requestTokenFromB2C(requestBody: Record<string, string>) {
    const data = await getHttpService().post(AUTH_TOKEN_URL, qs.stringify(requestBody), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;',
      },
    });

    AuthStorageService.clearPkceFlowData();
    AuthStorageService.saveAuthResponse(data as AuthResponse);
    return data;
  }

  private static async requestToken(requestBody: Record<string, string>) {
    if (this._useWarpAuth) {
      return this.requestTokenFromWarp(requestBody);
    }
    return this.requestTokenFromB2C(requestBody);
  }

  /**
   * The target URL is the page that the logout was triggered on, so the current location.
   * It's used as a link for on the 'Log in again' button on the post logout page.
   * */
  private static getTargetUrl(): string | undefined {
    const currentLocation = window?.location?.href;
    if (!currentLocation) {
      return undefined;
    }

    // Remove logout query parameter from currentLocation. That target URL should never include the logout query param.
    // See libs/core/frontend/shared/src/lib/feature/auth/use-auth.ts:81 for the intended usage of logout query param.
    // See https://jira01.com.stihlgroup.net/browse/WEB-7691 for the bug that made removing the logout query param necessary.
    const url = new URL(currentLocation);
    url.searchParams.delete('logout');
    return url.toString();
  }

  static getRefreshTokenBody = (): Record<string, string> | undefined => {
    if (!AUTH_CLIENT_ID) {
      throw new Error('No client id found');
    }

    const refreshToken = AuthStorageService.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    return {
      client_id: AUTH_CLIENT_ID,
      grant_type: 'refresh_token',
      scope: AUTH_SCOPE,
      refresh_token: refreshToken,
    };
  };

  private static getTokenBody = (queryCode?: string): Record<string, string> | undefined => {
    if (!AUTH_CLIENT_ID) {
      throw new Error('No client id found');
    }
    if (!AUTH_REDIRECT_URI) {
      throw new Error('No redirect uri found');
    }
    if (!queryCode) {
      throw new Error('Cannot perform token request without query code');
    }

    const { codeVerifier } = AuthStorageService.getPkceFlowData();
    if (codeVerifier) {
      return {
        code: queryCode,
        grant_type: 'authorization_code',
        code_verifier: codeVerifier,
        client_id: AUTH_CLIENT_ID,
        redirect_uri: AUTH_REDIRECT_URI,
        scope: AUTH_SCOPE,
      };
    }
  };
}

export const authService = AuthService.getInstance();
